/**
 * @description
 * Registry of vendor metadata parsers for offline radar records.
 *
 * One parser per device type from the IS TM device register (`wf_sktmdevices.type`). The map is
 * a literal list on purpose: adding a vendor means adding one require line here, next to the CSV
 * line that introduces the type - nothing is discovered by scanning.
 *
 * A parser is a pure module exporting `parse(txt) → { fields, warnings, serial? }`:
 * - `txt` is the decoded text of the uploaded vendor file
 * - `fields` uses **workflow field names** (`event_dt` µs since epoch, `lpnumber`, `lpcountry`,
 *   `speed_kmh`, `speed_limit`, `lane`, `gps_lat`, `gps_lon`, `locality`, `employee_num`) so the
 *   listener can merge them without translation; every field is optional - whatever the parser
 *   cannot read, the operator types in by hand
 * - `warnings` is an array of strings shown to the operator next to the prefilled form
 * - `serial` is the device serial number when the vendor file carries one - the import compares
 *   it against the device the operator picked and refuses a mismatch
 *
 * A device type with no parser is a valid state, not an error: the import then starts with an
 * empty form and the operator transcribes everything from the evidence photo (that is what the
 * generic `offlineRadar` type is for).
 */

const PARSERS = {
  // device type (wf_sktmdevices.type) → parser module; add a line per supported vendor
  trucam: require('./trucam.js'),
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * Runs the parser registered for the device type over the vendor metadata text.
 * Never throws: a crashing parser degrades to a fully manual import with a warning.
 * @param {string} device_type - type from the IS TM device register
 * @param {string} txt - decoded vendor metadata text
 * @returns {?{fields: object, warnings: Array<string>}} parse result, or null when no parser exists
 */
function parseMeta(device_type, txt) {
  const parser = PARSERS[device_type]
  if(!parser || typeof parser.parse !== 'function') return null
  try{
    const result = parser.parse(String(txt ?? ''))
    return {
      fields: (result && typeof result.fields === 'object' && result.fields) || {},
      warnings: Array.isArray(result?.warnings) ? result.warnings.map(w => String(w)) : [],
      serial: String(result?.serial ?? ''),
    }
  }catch(error) {
    return {
      fields: {},
      warnings: [`{{wf.sktzrecords.warning.ParserFailed}}: ${device_type} - ${error?.message || error}`],
      serial: '',
    }
  }
} // parseMeta

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * Runs every registered parser over the metadata text and returns those that could read a
 * device serial number out of it - the import uses this to identify the device (and thereby
 * the vendor) without the operator having to pick it.
 * @param {string} txt - decoded vendor metadata text
 * @returns {Array<{type: string, parsed: {fields: object, warnings: Array<string>, serial: string}}>}
 */
function detectSerials(txt) {
  const candidates = []
  for(const type of Object.keys(PARSERS)) {
    const parsed = parseMeta(type, txt)
    if(parsed && parsed.serial) candidates.push({ type, parsed })
  }
  return candidates
} // detectSerials

module.exports = { parseMeta, detectSerials, PARSERS }
